<script setup lang="ts">
  /**
   * Card - 03-components/13-cards + 13a-detail-card - OSCloud DS 1.1.170
   *
   * The row card is `.card` + `__aside`; the surface is a separate choice. Bare, the card is
   * the shadowed object; `details` makes it the flat hairline box (13a); `details` + `raised`
   * is bordered *and* shaded - the surface the selection summary bar sits on. All three take
   * an aside and become a flex row through `:has()`.
   */
  import { computed } from 'vue'

  type BfCardBarSurface = 'tint' | 'white'

  const props = withDefaults(
    defineProps<{
      /** The element the box renders as — a box that is a landmark is a `section`. */
      tag?: 'div' | 'section' | 'article'
      dark?: boolean
      accent?: boolean
      details?: boolean
      raised?: boolean
      /**
       * The hairline box takes the deeper shadow while the pointer is over it (13a, DS 1.1.170).
       * Pure emphasis: the box is not a target and gains no link, cursor or focus behaviour.
       * Only meaningful with `details`.
       */
      hoverable?: boolean
      /**
       * The feature-tile body (13a): media, title and body stacked at one --bf-sp-3 gap, in
       * place of the eyebrow/title/body flow. The `media` slot holds the tile's framed icon.
       */
      stack?: boolean
      /**
       * The soft tone of the flat box (13a, DS 1.1.58): the hairline gives way to a tint. For
       * read-once reference copy that nothing acts on — a security note, a policy line — so it
       * never carries an action row. Only meaningful with `details`.
       */
      soft?: boolean
      compactTitle?: boolean
      titleInline?: boolean
      eyebrow?: string
      title?: string
      /** The id a region's `aria-labelledby` points at. */
      titleId?: string
      /** Heading level of `__title` — the outline decides it, not the card. */
      titleTag?: 'h2' | 'h3' | 'h4'
      /**
       * The box's own title as a full-bleed strip across its top edge (13a): eyebrow type on a
       * tinted cap. It names the BOX, so it is never combined with a section header for the same
       * title, and it is not `__title` — that sits in the body and titles the card's content.
       */
      barTitle?: string
      /**
       * The title bar's second form (13a): a sentence under the eyebrow. With it, `barTitle`
       * becomes the eyebrow and this sentence is the heading, so `barTitleId`/`barTitleTag`
       * move onto it.
       */
      barHeading?: string
      /** The id a region's `aria-labelledby` points at. */
      barTitleId?: string
      /** Heading level — the outline decides it, not the card. */
      barTitleTag?: 'h2' | 'h3' | 'h4' | 'h5'
      /**
       * The cap's colour. `accent` is the directed default; `identifier` suits a column of boxes
       * where several orange caps would stripe the page, and the quieter two suit a secondary
       * box. Never a tone: the cap is a label, not a status.
       */
      barTitleTone?: 'accent' | 'identifier' | 'primary' | 'secondary'
      /**
       * The title bar's tint (13a). The footer bar follows it unless `footSurface` is set. The
       * one legal split is a white cap over a tinted foot — never the reverse, which leaves the
       * foot reading as unfinished body.
       */
      barSurface?: BfCardBarSurface
      footSurface?: BfCardBarSurface
    }>(),
    {
      tag: 'div',
      dark: false,
      accent: true,
      details: false,
      raised: false,
      hoverable: false,
      stack: false,
      soft: false,
      compactTitle: false,
      titleInline: false,
      eyebrow: '',
      title: '',
      titleId: undefined,
      titleTag: 'h3',
      barTitle: '',
      barHeading: '',
      barTitleId: undefined,
      barTitleTag: 'h3',
      barTitleTone: 'accent',
      barSurface: 'tint',
      footSurface: undefined,
    },
  )

  const SURFACE_TOKEN: Record<BfCardBarSurface, string> = {
    tint: 'var(--bf-blue-light)',
    white: 'var(--bf-white)',
  }

  /** The bars read their tint from two customs, so a token is the only thing ever set inline. */
  const surfaceStyle = computed(() => ({
    '--card-bar-surface': SURFACE_TOKEN[props.barSurface],
    '--card-foot-surface': SURFACE_TOKEN[props.footSurface ?? props.barSurface],
  }))
</script>

<template>
  <component
    :is="tag"
    class="card"
    :class="{
      'card--inverse': dark,
      'card--highlighted': accent,
      'card--details': details,
      'card--raised': raised,
      'card--hoverable': hoverable,
      'card--soft': soft,
      'card--compact-title': compactTitle,
      'card--title-inline': titleInline,
    }"
    :style="surfaceStyle"
  >
    <!-- The cap, when there is one: first child, one per box, and always full-bleed — its
         negative margins cancel the card's padding on three sides so it meets the border. -->
    <div
      v-if="barTitle || barHeading || $slots.barTitle"
      class="card__title-bar"
      :class="`card__title-bar--${barTitleTone}`"
    >
      <!-- Second form: the eyebrow over a sentence, stacked so a trailing action aligns to
           the whole head. The sentence is the heading; the eyebrow is its caption. -->
      <div v-if="barHeading" class="card__title-bar-text">
        <span v-if="barTitle" class="card__title-bar-title">{{ barTitle }}</span>
        <component :is="barTitleTag" :id="barTitleId" class="card__title-bar-heading">{{ barHeading }}</component>
      </div>
      <component :is="barTitleTag" v-else :id="barTitleId" class="card__title-bar-title">
        <slot name="barTitle">{{ barTitle }}</slot>
      </component>
      <div v-if="$slots.barAction" class="card__title-bar-action">
        <slot name="barAction" />
      </div>
    </div>

    <!-- The feature tile: media, title and body one --bf-sp-3 apart. -->
    <div v-if="stack" class="card__stack">
      <slot name="media" />
      <component :is="titleTag" v-if="title" :id="titleId" class="card__title">{{ title }}</component>
      <div class="card__body">
        <slot />
      </div>
    </div>

    <!-- With an aside the eyebrow and title move inside __body, so __body is the
         row's flexible column and __aside the trailing control. `titleInline` opts out:
         the title becomes a flex child of its own and sits beside the body. -->
    <div v-else-if="$slots.aside && !titleInline" class="card__body">
      <span v-if="eyebrow" class="card__eyebrow">{{ eyebrow }}</span>
      <component :is="titleTag" v-if="title" :id="titleId" class="card__title">{{ title }}</component>
      <slot />
    </div>
    <template v-else>
      <span v-if="eyebrow" class="card__eyebrow">{{ eyebrow }}</span>
      <component :is="titleTag" v-if="title" :id="titleId" class="card__title">{{ title }}</component>
      <div class="card__body">
        <slot />
      </div>
    </template>
    <div v-if="$slots.aside" class="card__aside">
      <slot name="aside" />
    </div>

    <!-- The box's foot: last child, one per box. -->
    <div v-if="$slots.footer" class="card__footer-bar">
      <slot name="footer" />
    </div>
  </component>
</template>

<style scoped lang="scss">
  .card {
    position: relative;
    overflow: hidden;
    border-radius: var(--bf-r-lg);
    padding: 22px 24px;
    background: var(--bf-white);
    box-shadow: var(--bf-shadow-1);

    &--highlighted::before {
      content: '';
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      width: 4px;
      background: var(--bf-orange-dark); /* the app layer draws the edge in the darker orange */
    }

    &--inverse {
      background: var(--bf-deep-blue);
      color: var(--bf-white);
      box-shadow: none;
    }

    /* The flat, read-only surface (13a): a hairline box instead of the shadowed object.
       The inset is NAMED because __title-bar bleeds through it with negative margins.
       Internal: set by this rule and by the narrow step below, never inline and never
       by a page. */
    &--details {
      --card-pad-block: 18px;
      --card-pad-inline: 22px;
      padding: var(--card-pad-block) var(--card-pad-inline);
      border: 1px solid var(--bf-line);
      box-shadow: none;
    }

    /* Bordered *and* shaded. Declared after --details so it wins the box-shadow at equal
       specificity, the way the design system orders the two modifiers. */
    &--details.card--raised {
      box-shadow: var(--bf-shadow-1);
    }

    /* Hoverable: the deeper shadow while the pointer is over the box. Composes with --raised
       (rest shadow-1, hover shadow-2) or stands alone (rest flat). Never a transform or a
       border-colour change — the selection card's orange edge means SELECTED. */
    &--details.card--hoverable {
      transition: box-shadow var(--bf-dur-fast) var(--bf-ease);
    }

    &--details.card--hoverable:hover {
      box-shadow: var(--bf-shadow-2);
    }

    /* Soft: the flat box on a tint instead of a hairline. The border stays (transparent) so the
       geometry is the hairline box's own. */
    &--soft {
      background: var(--bf-blue-light);
      border-color: transparent;
    }

    &--soft > .card__title-bar {
      background: transparent;
    }

    /* ─── Title bar: the box's own title, as a strip ─────────────────
       The negative margins cancel the card's padding on three sides so the bar meets the
       border; the bottom margin restores the content offset. On a --details box the bar
       cancels through --card-pad-*, so it follows the narrow step automatically. */
    &__title-bar {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--bf-sp-2);
      margin: -22px -24px 22px;
      padding: 12px 24px;
      border-bottom: 1px solid var(--bf-line);
      border-radius: var(--bf-r-lg) var(--bf-r-lg) 0 0;
      background: var(--card-bar-surface);
    }

    /* The second form: eyebrow over a sentence. One step under a section header, because the
       bar names the BOX, not a section of the page. */
    &__title-bar:has(.card__title-bar-heading) {
      padding-top: 16px;
      padding-bottom: 16px;
    }

    &__title-bar-text {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 0;
    }

    &__title-bar-heading {
      margin: 0;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-base);
      font-weight: var(--bf-fw-semibold);
      line-height: var(--bf-lh-tight);
      letter-spacing: var(--bf-tr-tight);
      overflow-wrap: anywhere;
      color: var(--bf-fg-1);
    }

    &--details > .card__title-bar {
      margin: calc(-1 * var(--card-pad-block)) calc(-1 * var(--card-pad-inline)) var(--card-pad-block);
      padding: 12px var(--card-pad-inline);
    }

    &__title-bar-title {
      margin: 0;
      font-family: var(--bf-font-display);
      font-size: var(--bf-fs-xs);
      font-weight: var(--bf-fw-bold);
      line-height: var(--bf-lh-snug);
      letter-spacing: var(--bf-tr-eyebrow);
      text-transform: uppercase;
      color: var(--bf-orange-dark);
    }

    &__title-bar--identifier .card__title-bar-title {
      color: var(--bf-deep-blue);
    }

    &__title-bar--primary .card__title-bar-title {
      color: var(--bf-fg-1);
    }

    &__title-bar--secondary .card__title-bar-title {
      color: var(--bf-fg-2);
    }

    &__title-bar-action {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      gap: var(--bf-sp-1);
    }

    &__eyebrow {
      display: block;
      font-family: var(--bf-font-display);
      font-size: 11px;
      font-weight: var(--bf-fw-bold);
      text-transform: uppercase;
      letter-spacing: 0.16em;
      color: var(--bf-orange-dark);
      margin-bottom: 8px;
    }

    &__title {
      font-family: var(--bf-font-display);
      font-weight: var(--bf-fw-bold);
      font-size: 22px;
      letter-spacing: var(--bf-tr-tight);
      line-height: 1.1;
      margin: 0 0 6px;
    }

    &--compact-title &__title {
      font-size: var(--bf-fs-sm);
      font-weight: var(--bf-fw-semibold);
      margin-bottom: 3px;
    }

    &--title-inline &__title {
      margin-bottom: 0;
    }

    /* The tile title drops a step from the elevated card's 22px, so it sits with the plan card's
       title rather than shouting inside a hairline box. Written for the stack only: the DS also
       applies it to a bare `--details > __title`, which would override `--compact-title` here. */
    &--details > .card__stack > .card__title {
      margin: 0;
      font-size: var(--bf-fs-md);
      font-weight: var(--bf-fw-semibold);
    }

    &__stack {
      display: flex;
      flex-direction: column;
      gap: var(--bf-sp-3);
      min-width: 0;
    }

    &__body {
      font-size: var(--bf-fs-sm);
      color: var(--bf-fg-2);
      line-height: 1.5;
    }

    /* ─── Footer bar: the box's foot ────────────────────────────────
       The title bar mirrored: negative margins cancel the padding on three sides and the top
       margin restores the content offset. Holds a closing remark or the box's action row.
       Reached through :deep() because a box's action row is often rendered by the child
       component that owns its state; it must still be the box's last block. */
    &--details :deep(.card__footer-bar) {
      margin: var(--card-pad-block) calc(-1 * var(--card-pad-inline)) calc(-1 * var(--card-pad-block));
      padding: 14px var(--card-pad-inline);
      border-top: 1px solid var(--bf-line);
      border-radius: 0 0 var(--bf-r-lg) var(--bf-r-lg);
      background: var(--card-foot-surface);
      font-size: var(--bf-fs-xs_5);
    }

    &--soft :deep(.card__footer-bar) {
      background: transparent;
    }

    &--inverse &__body {
      color: var(--bf-dusky-2);
    }

    /* Aside — a trailing control slot. Presence of __aside turns the card into a
       row (content + trailing control); absent, the card stays block as before. */
    &:has(> .card__aside) {
      display: flex;
      align-items: center;
      gap: var(--bf-sp-3);

      > .card__body {
        flex: 1;
        min-width: 0;
      }
    }

    &__aside {
      flex-shrink: 0;
      display: flex;
      align-items: center;
    }
  }

  /* Narrow step: below --bf-bp-sm the box drops to a square 16px inset. 22px is the widest
     single inset on a phone page, and the 4px difference is invisible once the box is the full
     width of the screen. One declaration pair — the title bar's bleed follows the names above,
     so this step cannot desynchronise it. */
  @media (max-width: 600px) {
    .card--details {
      --card-pad-block: 16px;
      --card-pad-inline: 16px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .card--details.card--hoverable {
      transition: none;
    }
  }

  @media (max-width: 480px) {
    .card:has(> .card__aside) {
      flex-direction: column;
      align-items: stretch;
    }
  }
</style>
